/**
 * Hangul → Latin: the Revised Romanization of Korean (Ministry of Culture and Tourism
 * Notice 2000-8), as published by the National Institute of Korean Language
 * (korean.go.kr, "Romanization of Korean"), with its transliteration rule (section 3 (8)).
 *
 * Syllables are decomposed arithmetically (The Unicode Standard §3.12, "Conjoining Jamo
 * Behavior"): for a syllable code point S, index = S − 0xAC00, initial = index ÷ 588,
 * vowel = (index mod 588) ÷ 28, final = index mod 28 — 19 × 21 × 28 syllables in all.
 *
 * Revised Romanization spells the standard pronunciation, so the sound changes at syllable
 * boundaries are applied first: liaison (국어 → gugeo), final-consonant neutralization
 * (벚꽃 → beotkkot), nasalization (백마 → Baengma, 종로 → Jongno, 왕십리 → Wangsimni),
 * liquidization (신라 → Silla, 별내 → Byeollae), aspiration (좋고 → joko, 잡혀 → japyeo) and
 * palatalization (해돋이 → haedoji, 같이 → gachi). Tensification is never written
 * (압구정 → Apgujeong), and in nouns ㅎ after ㄱ, ㄷ, ㅂ stays separate (묵호 → Mukho), which
 * is an option here because spelling alone cannot tell a noun from a verb.
 *
 * Not applied, because they depend on where the parts of a compound meet rather than on
 * spelling: the inserted ㄴ of 학여울 (Hangnyeoul) and 알약 (allyak), and the ㄴㄴ of compounds
 * such as 신문로 (Sinmunno).
 */

export interface Syllable {
  initial: string;
  vowel: string;
  final: string;
}

const INITIALS = [..."ㄱㄲㄴㄷㄸㄹㅁㅂㅃㅅㅆㅇㅈㅉㅊㅋㅌㅍㅎ"];
const VOWELS = [..."ㅏㅐㅑㅒㅓㅔㅕㅖㅗㅘㅙㅚㅛㅜㅝㅞㅟㅠㅡㅢㅣ"];
const FINALS = [
  "",
  ..."ㄱㄲㄳㄴㄵㄶㄷㄹㄺㄻㄼㄽㄾㄿㅀㅁㅂㅄㅅㅆㅇㅈㅊㅋㅌㅍㅎ",
];

const FIRST_SYLLABLE = 0xac_00;
const LAST_SYLLABLE = 0xd7_a3;

export function isHangulSyllable(char: string): boolean {
  const code = char.codePointAt(0) ?? 0;
  return code >= FIRST_SYLLABLE && code <= LAST_SYLLABLE;
}

export function decompose(char: string): Syllable {
  const index = (char.codePointAt(0) ?? FIRST_SYLLABLE) - FIRST_SYLLABLE;
  return {
    initial: INITIALS[Math.floor(index / 588)],
    vowel: VOWELS[Math.floor((index % 588) / 28)],
    final: FINALS[index % 28],
  };
}

/* ------------------------------------------------------------------ letter values */

export const RR_VOWELS: Record<string, string> = {
  ㅏ: "a",
  ㅐ: "ae",
  ㅑ: "ya",
  ㅒ: "yae",
  ㅓ: "eo",
  ㅔ: "e",
  ㅕ: "yeo",
  ㅖ: "ye",
  ㅗ: "o",
  ㅘ: "wa",
  ㅙ: "wae",
  ㅚ: "oe",
  ㅛ: "yo",
  ㅜ: "u",
  ㅝ: "wo",
  ㅞ: "we",
  ㅟ: "wi",
  ㅠ: "yu",
  ㅡ: "eu",
  ㅢ: "ui",
  ㅣ: "i",
};

/** Initial consonants; ㄹ is r here and becomes l after an l. */
export const RR_INITIALS: Record<string, string> = {
  ㄱ: "g",
  ㄲ: "kk",
  ㄴ: "n",
  ㄷ: "d",
  ㄸ: "tt",
  ㄹ: "r",
  ㅁ: "m",
  ㅂ: "b",
  ㅃ: "pp",
  ㅅ: "s",
  ㅆ: "ss",
  ㅇ: "",
  ㅈ: "j",
  ㅉ: "jj",
  ㅊ: "ch",
  ㅋ: "k",
  ㅌ: "t",
  ㅍ: "p",
  ㅎ: "h",
};

/** The seven sounds a syllable can end in, once neutralized. */
export const RR_FINALS: Record<string, string> = {
  ㄱ: "k",
  ㄴ: "n",
  ㄷ: "t",
  ㄹ: "l",
  ㅁ: "m",
  ㅂ: "p",
  ㅇ: "ng",
};

/** Section 3 (8): every letter as written; ㄱ, ㄷ, ㅂ, ㄹ always g, d, b, l. */
export const TRANSLIT_INITIALS: Record<string, string> = {
  ...RR_INITIALS,
  ㄹ: "l",
};

export const TRANSLIT_FINALS: Record<string, string> = {
  "": "",
  ㄱ: "g",
  ㄲ: "kk",
  ㄳ: "gs",
  ㄴ: "n",
  ㄵ: "nj",
  ㄶ: "nh",
  ㄷ: "d",
  ㄹ: "l",
  ㄺ: "lg",
  ㄻ: "lm",
  ㄼ: "lb",
  ㄽ: "ls",
  ㄾ: "lt",
  ㄿ: "lp",
  ㅀ: "lh",
  ㅁ: "m",
  ㅂ: "b",
  ㅄ: "bs",
  ㅅ: "s",
  ㅆ: "ss",
  ㅇ: "ng",
  ㅈ: "j",
  ㅊ: "ch",
  ㅋ: "k",
  ㅌ: "t",
  ㅍ: "p",
  ㅎ: "h",
};

/* -------------------------------------------------------------- sound changes */

/** Double finals as [stays, moves]: 앉아 → 안자. */
const SPLIT: Record<string, [string, string]> = {
  ㄳ: ["ㄱ", "ㅅ"],
  ㄵ: ["ㄴ", "ㅈ"],
  ㄶ: ["ㄴ", "ㅎ"],
  ㄺ: ["ㄹ", "ㄱ"],
  ㄻ: ["ㄹ", "ㅁ"],
  ㄼ: ["ㄹ", "ㅂ"],
  ㄽ: ["ㄹ", "ㅅ"],
  ㄾ: ["ㄹ", "ㅌ"],
  ㄿ: ["ㄹ", "ㅍ"],
  ㅀ: ["ㄹ", "ㅎ"],
  ㅄ: ["ㅂ", "ㅅ"],
};

/** Every final, before a consonant or at the end of a word, as one of seven sounds. */
const NEUTRAL: Record<string, string> = {
  ㄱ: "ㄱ",
  ㄲ: "ㄱ",
  ㅋ: "ㄱ",
  ㄳ: "ㄱ",
  ㄺ: "ㄱ",
  ㄴ: "ㄴ",
  ㄵ: "ㄴ",
  ㄶ: "ㄴ",
  ㄷ: "ㄷ",
  ㅅ: "ㄷ",
  ㅆ: "ㄷ",
  ㅈ: "ㄷ",
  ㅊ: "ㄷ",
  ㅌ: "ㄷ",
  ㅎ: "ㄷ",
  ㄹ: "ㄹ",
  ㄼ: "ㄹ",
  ㄽ: "ㄹ",
  ㄾ: "ㄹ",
  ㅀ: "ㄹ",
  ㅁ: "ㅁ",
  ㄻ: "ㅁ",
  ㅂ: "ㅂ",
  ㅍ: "ㅂ",
  ㅄ: "ㅂ",
  ㄿ: "ㅂ",
  ㅇ: "ㅇ",
};

const ASPIRATED: Record<string, string> = {
  ㄱ: "ㅋ",
  ㄷ: "ㅌ",
  ㅂ: "ㅍ",
  ㅈ: "ㅊ",
};

const NASALIZED: Record<string, string> = { ㄱ: "ㅇ", ㄷ: "ㄴ", ㅂ: "ㅁ" };

const OBSTRUENTS = new Set(["ㄱ", "ㄷ", "ㅂ"]);
const H_FINALS = new Set(["ㅎ", "ㄶ", "ㅀ"]);

export type SoundChange =
  | "liaison"
  | "nasalization"
  | "liquidization"
  | "aspiration"
  | "palatalization"
  | "h-dropped";

interface Boundary {
  final: string;
  initial: string;
  change?: SoundChange;
}

export interface RomanizeOptions {
  /** Keep ㅎ after ㄱ, ㄷ, ㅂ as h, the rule for nouns: 묵호 → Mukho. */
  nounH: boolean;
}

/** The neutralized sound of a final before a consonant (밟- and ㄺ before ㄱ are the exceptions). */
function neutral(final: string, next: string, syllable: string): string {
  if (final === "ㄼ" && syllable === "밟") {
    return "ㅂ";
  }
  if (final === "ㄺ" && next === "ㄱ") {
    return "ㄹ";
  }
  return NEUTRAL[final] ?? final;
}

function palatal(consonant: string, vowel: string): string {
  if (vowel !== "ㅣ") {
    return consonant;
  }
  if (consonant === "ㄷ") {
    return "ㅈ";
  }
  return consonant === "ㅌ" ? "ㅊ" : consonant;
}

/** Final + silent ㅇ: the final consonant moves across (liaison). */
function beforeVowel(final: string, vowel: string): Boundary {
  if (final === "ㅇ") {
    return { final, initial: "ㅇ" };
  }
  if (final === "ㅎ") {
    return { final: "", initial: "ㅇ", change: "h-dropped" };
  }
  const split = SPLIT[final];
  if (split) {
    const [stays, moves] = split;
    if (moves === "ㅎ") {
      return { final: "", initial: stays, change: "h-dropped" };
    }
    const moved = palatal(moves, vowel);
    return {
      final: stays,
      initial: moved,
      change: moved === moves ? "liaison" : "palatalization",
    };
  }
  const moved = palatal(final, vowel);
  return {
    final: "",
    initial: moved,
    change: moved === final ? "liaison" : "palatalization",
  };
}

/** Final + ㅎ: aspiration, or ㅎ kept after ㄱ, ㄷ, ㅂ in nouns. */
function beforeH(
  final: string,
  vowel: string,
  options: RomanizeOptions
): Boundary {
  if (final === "ㅈ") {
    return { final: "", initial: "ㅊ", change: "aspiration" };
  }
  const split = SPLIT[final];
  if (split && ASPIRATED[split[1]]) {
    return {
      final: split[0],
      initial: ASPIRATED[split[1]],
      change: "aspiration",
    };
  }
  const sound = NEUTRAL[final] ?? final;
  if (!OBSTRUENTS.has(sound)) {
    return { final: sound, initial: "ㅎ" };
  }
  if (options.nounH) {
    return { final: sound, initial: "ㅎ" };
  }
  const aspirated = ASPIRATED[sound];
  const result = palatal(aspirated, vowel);
  return {
    final: "",
    initial: result,
    change: result === aspirated ? "aspiration" : "palatalization",
  };
}

/** A final containing ㅎ before a consonant: 좋고 → joko, 놓는 → nonneun, 뚫는 → ttulleun. */
function hBeforeConsonant(final: string, initial: string): Boundary | null {
  if (!H_FINALS.has(final)) {
    return null;
  }
  const stays = final === "ㅎ" ? "" : SPLIT[final][0];
  // ㅎ aspirates a following ㄱ, ㄷ or ㅈ (Standard Pronunciation art. 12); ㅂ never follows it.
  if (initial !== "ㅂ" && ASPIRATED[initial]) {
    return { final: stays, initial: ASPIRATED[initial], change: "aspiration" };
  }
  if (initial === "ㅅ") {
    return { final: stays, initial };
  }
  if (initial === "ㄴ") {
    if (stays === "ㄹ") {
      return { final: "ㄹ", initial: "ㄹ", change: "liquidization" };
    }
    return { final: "ㄴ", initial, change: "nasalization" };
  }
  return null;
}

/** Two consonants meeting: neutralization, nasalization and liquidization. */
function consonantMeeting(sound: string, initial: string): Boundary {
  if (initial === "ㄹ") {
    if (sound === "ㄹ") {
      return { final: "ㄹ", initial: "ㄹ" };
    }
    if (sound === "ㄴ") {
      return { final: "ㄹ", initial: "ㄹ", change: "liquidization" };
    }
    const final = NASALIZED[sound] ?? sound;
    return { final, initial: "ㄴ", change: "nasalization" };
  }
  if (initial === "ㄴ" || initial === "ㅁ") {
    if (NASALIZED[sound]) {
      return { final: NASALIZED[sound], initial, change: "nasalization" };
    }
    if (sound === "ㄹ" && initial === "ㄴ") {
      return { final: "ㄹ", initial: "ㄹ", change: "liquidization" };
    }
  }
  return { final: sound, initial };
}

function boundary(
  current: Syllable,
  next: Syllable,
  syllableText: string,
  options: RomanizeOptions
): Boundary {
  const { final } = current;
  if (final === "") {
    return { final: "", initial: next.initial };
  }
  if (next.initial === "ㅇ") {
    return beforeVowel(final, next.vowel);
  }
  if (next.initial === "ㅎ") {
    return beforeH(final, next.vowel, options);
  }
  const withH = hBeforeConsonant(final, next.initial);
  if (withH) {
    return withH;
  }
  return consonantMeeting(
    neutral(final, next.initial, syllableText),
    next.initial
  );
}

/* ------------------------------------------------------------------- romanizing */

export interface ChangeNote {
  /** The two syllables, as written. */
  syllables: string;
  change: SoundChange;
  /** Written and pronounced: "ㄱ+ㅁ → ㅇ+ㅁ". */
  from: string;
  to: string;
}

interface Romanized {
  /** Per syllable: onset, vowel and coda strings. */
  parts: { onset: string; vowel: string; coda: string }[];
  changes: ChangeNote[];
}

function romanizeSyllables(
  chars: readonly string[],
  options: RomanizeOptions
): Romanized {
  const syllables = chars.map(decompose);
  const initials = syllables.map((each) => each.initial);
  const finals = syllables.map((each) => each.final);
  const changes: ChangeNote[] = [];
  for (let index = 0; index < syllables.length - 1; index++) {
    const result = boundary(
      { ...syllables[index], final: finals[index] },
      { ...syllables[index + 1], initial: initials[index + 1] },
      chars[index],
      options
    );
    if (result.change) {
      changes.push({
        syllables: chars[index] + chars[index + 1],
        change: result.change,
        from: `${finals[index]}+${initials[index + 1]}`,
        to: `${result.final || "∅"}+${result.initial}`,
      });
    }
    finals[index] = result.final;
    initials[index + 1] = result.initial;
  }
  const lastIndex = syllables.length - 1;
  finals[lastIndex] = neutral(finals[lastIndex], "", chars[lastIndex]);
  const parts = syllables.map((syllable, index) => {
    const previous = index > 0 ? finals[index - 1] : "";
    const onset =
      initials[index] === "ㄹ" && previous === "ㄹ"
        ? "l"
        : RR_INITIALS[initials[index]];
    return {
      onset,
      vowel: RR_VOWELS[syllable.vowel],
      coda: finals[index]
        ? (RR_FINALS[NEUTRAL[finals[index]] ?? finals[index]] ?? "")
        : "",
    };
  });
  return { parts, changes };
}

const DIGRAPH_VOWEL_START = /^(?:eo|eu|ae|oe|ui)/;

/** Section 3 (2): a hyphen where two syllables could be read another way (Jung-ang, Ban-gudae, Se-un). */
function needsHyphen(
  left: { onset: string; vowel: string; coda: string },
  right: { onset: string; vowel: string; coda: string }
): boolean {
  if (right.onset === "") {
    if (left.coda === "ng") {
      return true;
    }
    if (left.coda === "") {
      const joint = left.vowel.slice(-1) + right.vowel.charAt(0);
      return DIGRAPH_VOWEL_START.test(joint);
    }
    return false;
  }
  return left.coda === "n" && right.onset === "g";
}

export interface RrOptions extends RomanizeOptions {
  hyphenate: boolean;
}

/** One run of Hangul syllables in Revised Romanization. */
export function romanizeWord(
  word: string,
  options: RrOptions
): { text: string; changes: ChangeNote[] } {
  const { parts, changes } = romanizeSyllables([...word], options);
  let text = "";
  for (const [index, part] of parts.entries()) {
    if (index > 0 && options.hyphenate && needsHyphen(parts[index - 1], part)) {
      text += "-";
    }
    text += part.onset + part.vowel + part.coda;
  }
  return { text, changes };
}

/** One syllable on its own, as given names are written: 복 → bok, 빛 → bit. */
function isolatedSyllable(char: string): string {
  const { parts } = romanizeSyllables([char], { nounH: true });
  const [part] = parts;
  return part.onset + part.vowel + part.coda;
}

/* ------------------------------------------------------------ transliteration */

const AMBIGUOUS_FINALS = Object.values(TRANSLIT_FINALS);
const AMBIGUOUS_INITIALS = Object.values(TRANSLIT_INITIALS).filter(
  (value) => value !== ""
);

/** True when the letters between two vowels could be split into final + initial two ways. */
function ambiguousCluster(cluster: string): boolean {
  let splits = 0;
  for (let cut = 0; cut <= cluster.length; cut++) {
    if (
      AMBIGUOUS_FINALS.includes(cluster.slice(0, cut)) &&
      AMBIGUOUS_INITIALS.includes(cluster.slice(cut))
    ) {
      splits += 1;
    }
  }
  return splits > 1;
}

/** Section 3 (8): letter by letter, a hyphen for silent ㅇ and wherever the split is ambiguous. */
export function transliterateWord(word: string): string {
  let text = "";
  let previousFinal = "";
  for (const [index, char] of [...word].entries()) {
    const { initial, vowel, final } = decompose(char);
    const onset = TRANSLIT_INITIALS[initial];
    if (index > 0) {
      if (initial === "ㅇ") {
        text += "-";
      } else if (ambiguousCluster(previousFinal + onset)) {
        text += "-";
      }
    }
    previousFinal = TRANSLIT_FINALS[final];
    text += onset + RR_VOWELS[vowel] + previousFinal;
  }
  return text;
}

/* -------------------------------------------------------------------- whole text */

export type KoreanMode = "rr" | "translit";

export interface KoreanOptions extends RrOptions {
  capitalize: boolean;
  personalName: boolean;
}

const HANGUL_RUN = /[가-힣]+/g;
const LINE_BREAK = /\r?\n/;
const SPACES = /\s+/g;

/** Two-syllable family names, which keep both syllables before the space. */
const TWO_SYLLABLE_SURNAMES = new Set([
  "남궁",
  "황보",
  "제갈",
  "선우",
  "독고",
  "사공",
  "서문",
]);

function capitalizeFirst(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1);
}

/** Section 3 (4): family name, a space, then the given name with no sound changes between its syllables. */
export function romanizeName(name: string): string | null {
  const syllables = [...name.replace(SPACES, "")];
  if (syllables.length < 2 || !syllables.every(isHangulSyllable)) {
    return null;
  }
  const familyLength =
    syllables.length > 2 &&
    TWO_SYLLABLE_SURNAMES.has(syllables.slice(0, 2).join(""))
      ? 2
      : 1;
  const family = syllables
    .slice(0, familyLength)
    .map(isolatedSyllable)
    .join("");
  const given = syllables.slice(familyLength).map(isolatedSyllable).join("");
  return `${capitalizeFirst(family)} ${capitalizeFirst(given)}`;
}

export interface KoreanResult {
  text: string;
  changes: ChangeNote[];
}

export function romanizeKorean(
  text: string,
  mode: KoreanMode,
  options: KoreanOptions
): KoreanResult {
  const changes: ChangeNote[] = [];
  const romanizeRuns = (line: string) =>
    line.replace(HANGUL_RUN, (word) => {
      if (mode === "translit") {
        return transliterateWord(word);
      }
      const result = romanizeWord(word, options);
      changes.push(...result.changes);
      return options.capitalize ? capitalizeFirst(result.text) : result.text;
    });
  if (mode === "rr" && options.personalName) {
    const lines = text
      .split(LINE_BREAK)
      .map((line) => romanizeName(line.trim()) ?? romanizeRuns(line));
    return { text: lines.join("\n"), changes };
  }
  return { text: romanizeRuns(text), changes };
}
